import type { AddonGroup } from '~/models';

interface UseAddonGroup {
  allMandatoriesWereSelected: () => boolean;
  normalize: () => void;
  updateDependents: () => void;
  cartAddonGroups: () => AddonGroup[];
  total: () => number;
  drsTotal: () => number;
}

const useAddonGroup = (groups: AddonGroup[]): UseAddonGroup => {
  const allMandatoriesWereSelected = (): boolean => {
    return !groups
      .filter(({ visible, minChoices }) => visible && minChoices > 0)
      .some((item) => {
        const choices = item.addons
          .map(({ quantity }) => quantity)
          .reduce((acc, curr) => acc + curr, 0);

        return choices < item.minChoices;
      });
  };

  const normalize = (): void => {
    groups.forEach((group) => {
      group.visible =
        group.dependencies.length === 0 && group.addons.length > 0;
      group.total = 0;
      group.addons.forEach((addon) => {
        addon.freeQuantity = 0;
        addon.quantity = 0;
      });

      if (group.visible) {
        return;
      }

      const dependencies = group.dependencies;
      group.dependencies = [];

      dependencies.forEach((dependency) => {
        const addon = groups
          .find((item) => item.uuid === dependency.addongroup?.uuid)
          ?.addons.find((addon) => addon.uuid === dependency.uuid);

        if (addon) {
          addon.addongroup = dependency.addongroup;
          group.dependencies.push(addon);
        }
      });
    });
  };

  const updateDependents = (): void => {
    for (const group of groups) {
      if (group.dependencies.length === 0) {
        continue;
      }

      group.visible = group.dependencies.some((item) => item.selected);

      if (group.visible) {
        continue;
      }

      group.addons.forEach((addon) => {
        addon.freeQuantity = 0;
        addon.quantity = 0;
        addon.selected = false;
      });

      group.total = 0;
    }
  };

  const cartAddonGroups = (): AddonGroup[] => {
    return groups
      .map((group) => ({
        ...group,
        addons: group.addons.filter((addon) => addon.selected)
      }))
      .filter((group) => group.addons.length > 0);
  };

  const total = (): number => {
    return groups
      .map(({ total }) => total)
      .reduce((acc, curr) => acc + curr, 0);
  };

  const drsTotal = (): number => {
    return groups
      .map(({ addons }) =>
        addons.reduce(
          (drs, addon) =>
            drs +
            (addon.quantity - addon.freeQuantity) * (addon.drsDeposit || 0),
          0
        )
      )
      .reduce((acc, curr) => acc + curr, 0);
  };

  return {
    allMandatoriesWereSelected,
    normalize,
    updateDependents,
    cartAddonGroups,
    total,
    drsTotal
  };
};

export { useAddonGroup };
